import { CreatePaymentParameters, Payment, PaymentBillDetails, usePayments } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { convertDateTimeToDateString } from '@melio/platform-utils';
import { useState } from 'react';

import { getDefaultMemo } from './BatchPayments.activity.utils';
import { SchedulePaymentIntent } from './types';

const getCreatePaymentPayload = (
  extendedPaymentIntent: SchedulePaymentIntent,
  shouldIncludeDeliveryDate: boolean
): CreatePaymentParameters | undefined => {
  const { paymentIntent, vendor } = extendedPaymentIntent;

  const vendorId = vendor.id;

  const {
    fundingSourceId,
    deliveryMethodId,
    noteToVendor,
    effectiveScheduledDate: scheduledDate,
    effectiveDeliveryDate: deliveryDate,
    billPayments,
    selectedDeliveryPreferenceType: deliveryPreference,
    requireWithdrawApproval,
    requireDepositApproval,
    paymentPurpose,
  } = paymentIntent;

  if (!fundingSourceId || !billPayments || !deliveryMethodId || !deliveryPreference || !scheduledDate || !vendorId) {
    return undefined;
  }

  const paymentBillsInfo = billPayments.map(
    (billPayment): PaymentBillDetails => ({
      id: billPayment.billId,
      paymentAmount: billPayment.amount,
    })
  );

  const createPaymentParameters: CreatePaymentParameters = {
    scheduledDate,
    deliveryDate: shouldIncludeDeliveryDate ? deliveryDate?.toISOString() : undefined,
    deliveryMethodId,
    fundingSourceId,
    vendorId,
    noteToVendor: noteToVendor || getDefaultMemo(extendedPaymentIntent),
    deliveryPreference,
    paymentBillsInfo,
    requireWithdrawApproval,
    requireDepositApproval,
    paymentPurpose,
  };

  return createPaymentParameters;
};

export const useSubmitBatchPayments = () => {
  const [confirmedPaymentIds, setConfirmedPaymentIds] = useState<Payment['id'][]>();
  const { batchCreate: createBatchPayments } = usePayments({ enabled: false });
  const [addDeliveryDateToCreateAndUpdate] = useFeature<boolean>(
    FeatureFlags.AddDeliveryDateToCreateAndUpdatePayment,
    false
  );
  const createPayments = async ({ paymentIntents }: { paymentIntents?: SchedulePaymentIntent[] }) => {
    if (!paymentIntents) {
      return;
    }

    const isDefined = (value: unknown): value is CreatePaymentParameters => !!value;
    const prepareSchedulingDates = (data: CreatePaymentParameters) =>
      convertDateTimeToDateString(data, ['scheduledDate', 'deliveryDate']);

    const payments = paymentIntents
      .map((paymentIntent) => getCreatePaymentPayload(paymentIntent, addDeliveryDateToCreateAndUpdate))
      .filter(isDefined)
      .map(prepareSchedulingDates);

    const createdPaymentsIds = await createBatchPayments({ payments }).then((results) => results.map(({ id }) => id));

    if (!createdPaymentsIds.length) {
      throw new Error('No payment has created');
    }

    setConfirmedPaymentIds(createdPaymentsIds);
  };

  return { createPayments, confirmedPaymentIds };
};
